/**
 * Infered type from the backend definition
 * @see https://github.com/Perion-Hivestack/hs-avails-api/blob/32c66153dba3462b6f537d4d2dd5d6382a311106/app/services/availsservice.py#L1107
 */
export type AvailsPurchaseTypes =
  | 'GUARANTEED_LINEITEM'
  | 'GUARANTEED_DEALS'
  | 'UNGUARANTEED'
  | 'HOUSE_AD'
  | 'CADENCE_BUY'
  | 'BONUS'
  | 'PREFERRED'
  | 'TAKEOVER';

/**
 * Priority of purchase types returned by the avails API (different syntax from PurchaseTypesEnum *sad*)
 */
export const AVAILS_PURCHASE_TYPE_PRIORITY: Record<AvailsPurchaseTypes, number> = Object.freeze({
  TAKEOVER: 0,
  CADENCE_BUY: 1,
  GUARANTEED_LINEITEMS: 2,
  GUARANTEED_DEALS: 2,
  PREFERRED: 3,
  UNGUARANTEED: 4,
  BONUS: 5,
  HOUSE_AD: 6,
});

export interface ITotalImpressionsByUnitAPI {
  overbooked_reserved_impressions_by_unit: { [id: string]: number };
  booked_impressions_by_purchase_type_by_unit: Record<AvailsPurchaseTypes, { [id: string]: number }>;
  unreserved_impressions_by_unit: { [id: string]: number };
  reserved_impressions_by_unit: { [id: string]: number };
  impression_capacity_by_unit: { [id: string]: number };
  max_cadence: { [id: string]: number };
  max_historical_cadence: { [id: string]: number };
  end_date: string;
  start_date: string;
}

export interface IImpressionsForUnit {
  impressionCapacity: number;
  totalImpressions: number;
  reservedImpressions: number;
  unreservedImpressions: number;
  bookedImpressions: number[];
  overbookedReservedImpressions: number;
  maxCadence: number;
  maxHistoricalCadence: number;
}

export default class TotalImpressionsByUnit {
  startDate: string;

  endDate: string;

  impressionCapacityByUnit: { [id: string]: number };

  reservedImpressionsByUnit: { [id: string]: number };

  unreservedImpressionsByUnit: { [id: string]: number };

  bookedImpressionsByPurchaseTypeByUnit: Record<AvailsPurchaseTypes, { [id: string]: number }>;

  overbookedReservedImpressionsByUnit: { [id: string]: number };

  maxCadence: { [id: string]: number } = {};

  maxHistoricalCadence: { [id: string]: number } = {};

  apiObject?: ITotalImpressionsByUnitAPI;

  constructor(
    startDate: string,
    endDate: string,
    impressionCapacityByUnit: { [id: string]: number },
    reservedImpressionsByUnit: { [id: string]: number },
    unreservedImpressionsByUnit: { [id: string]: number },
    bookedImpressionsByPurchaseTypeByUnit: Record<AvailsPurchaseTypes, { [id: string]: number }>,
    overbookedReservedImpressionsByUnit: { [id: string]: number },
    maxCadence: { [id: string]: number },
    maxHistoricalCadence: { [id: string]: number },
  ) {
    this.startDate = startDate;
    this.endDate = endDate;
    this.impressionCapacityByUnit = impressionCapacityByUnit;
    this.reservedImpressionsByUnit = reservedImpressionsByUnit;
    this.unreservedImpressionsByUnit = unreservedImpressionsByUnit;
    this.bookedImpressionsByPurchaseTypeByUnit = bookedImpressionsByPurchaseTypeByUnit;
    this.overbookedReservedImpressionsByUnit = overbookedReservedImpressionsByUnit;
    this.maxCadence = maxCadence;
    this.maxHistoricalCadence = maxHistoricalCadence;
    this.apiObject = null;
  }

  impressionsForUnit(unitID: number): IImpressionsForUnit {
    const impressionCapacity = this.impressionCapacityByUnit[unitID] || 0;
    const reservedImpressions = this.reservedImpressionsByUnit[unitID] || 0;
    const unreservedImpressions = this.unreservedImpressionsByUnit[unitID] || 0;
    const maxCadence = this.maxCadence[unitID] || 0;
    const maxHistoricalCadence = this.maxHistoricalCadence[unitID] || 0;
    let overbookedReservedImpressions = 0;
    if (this.overbookedReservedImpressionsByUnit !== undefined) {
      overbookedReservedImpressions = this.overbookedReservedImpressionsByUnit[unitID] || 0;
    }

    const bookedImpressions: number[] = [];
    if (this.bookedImpressionsByPurchaseTypeByUnit !== undefined) {
      const purchaseTypes = Object.keys(this.bookedImpressionsByPurchaseTypeByUnit);
      purchaseTypes.sort();
      purchaseTypes.forEach((purchaseType) => {
        const unitImpressionsMapping = this.bookedImpressionsByPurchaseTypeByUnit[purchaseType];
        bookedImpressions.push(unitImpressionsMapping[unitID] || 0);
      });
    }

    const totalImpressions = reservedImpressions + unreservedImpressions;

    return {
      impressionCapacity,
      totalImpressions,
      reservedImpressions,
      unreservedImpressions,
      bookedImpressions,
      overbookedReservedImpressions,
      maxCadence,
      maxHistoricalCadence,
    };
  }

  get unitIDs(): string[] {
    return this.impressionCapacityByUnit !== undefined ? Object.keys(this.impressionCapacityByUnit) : [];
  }

  static fromApi(apiObject: ITotalImpressionsByUnitAPI): TotalImpressionsByUnit {
    if (apiObject !== undefined) {
      const totalImpressionsByUnit = new TotalImpressionsByUnit(
        apiObject.start_date,
        apiObject.end_date,
        apiObject.impression_capacity_by_unit,
        apiObject.reserved_impressions_by_unit,
        apiObject.unreserved_impressions_by_unit,
        apiObject.booked_impressions_by_purchase_type_by_unit,
        apiObject.overbooked_reserved_impressions_by_unit,
        apiObject.max_cadence,
        apiObject.max_historical_cadence,
      );
      totalImpressionsByUnit.apiObject = apiObject;
      return totalImpressionsByUnit;
    }
    return new TotalImpressionsByUnit(null, null, null, null, null, null, null, null, null);
  }
}
